import { useState } from "react";

import type { GroupBase, StylesConfig } from "react-select";
import CreatableSelect, { CreatableProps } from "react-select/creatable";

import ClickableMultiValue from "./ClickableMultiValue";
import Input from "./Input";
import MultiValueContainer from "./MultiValueContainer";
import ValueContainer from "./ValueContainer";

type Option = { label: string; value: string };

type ExtraProps = {
  name: string;
  value: Option[];
  focus: (name: string) => void;
  canCreateOption: (value: string, name: string) => boolean;
  onCreateOption: (value: string) => void;
  onRemoveKeyword: (value: string) => void;
  onKeywordsPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  error?: string | null;
  inputId?: string;
  isDisabled?: boolean;
  toggledBackgroundColor?: string;
  styles?: StylesConfig<Option, true, GroupBase<Option>>;
};

type Props = ExtraProps &
  Omit<
    CreatableProps<Option, true, GroupBase<Option>>,
    | "options"
    | "value"
    | "styles"
    | "onKeyDown"
    | "className"
    | "classNamePrefix"
  > & {
    options?: Option[];
  };

const CreatableSelectWithCopyPaste: React.FC<Props> = (props) => {
  const [toggledOptions, setToggledOptions] = useState<string[]>([]);
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [lastClickedKeyword, setLastClickedKeyword] = useState<string | null>(
    null
  );
  const [editingKeyword, setEditingKeyword] = useState<string | null>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "a":
        if (e.ctrlKey || e.metaKey) {
          if (toggledOptions.length > 0) {
            setToggledOptions([]);
          } else {
            setToggledOptions(props.value.map((v) => v.value));
          }
        }
        props.focus(props.name);
        break;

      case "c":
        if (e.ctrlKey || e.metaKey) {
          navigator.clipboard.writeText(toggledOptions.join(","));
        }
        break;

      case "Enter":
        if (editingKeyword) {
          const currentValue =
            (e.target as HTMLInputElement | null)?.value ?? "";
          if (props.canCreateOption(currentValue, props.name)) {
            props.onRemoveKeyword(editingKeyword);
          }
          setEditingKeyword(null);
        }
        props.focus(props.name);
        break;

      case "Escape":
        setEditingKeyword(null);
        props.focus(props.name);
        break;

      case "Backspace":
        props.focus(props.name);
        // Handle deletion of multiple selected keywords
        if (toggledOptions.length > 0) {
          toggledOptions.forEach((k) => {
            props.onRemoveKeyword(k);
          });
          // If we don't prevent default, we get regular
          // behaviour which is to delete only one option
          e.preventDefault();
          setToggledOptions([]);
        }
        break;
    }
  };

  const handleMultiValueClick = (
    e: React.MouseEvent,
    componentProps: { data: Option }
  ) => {
    const now = Date.now();
    const clickedKeyword = componentProps.data.value;

    // check time between first and second click, workaround to not trigger click twice
    // as will be the case if onDoubleClick is used
    if (now - lastClickTime < 250 && clickedKeyword === lastClickedKeyword) {
      setEditingKeyword(clickedKeyword);
      setToggledOptions([]);
    } else {
      setEditingKeyword("");
      if (e.ctrlKey || e.metaKey) {
        if (toggledOptions.includes(componentProps.data.value)) {
          setToggledOptions(
            toggledOptions.filter((v) => v !== componentProps.data.value)
          );
        } else {
          setToggledOptions(toggledOptions.concat([componentProps.data.value]));
        }
      } else {
        if (
          toggledOptions.includes(componentProps.data.value) &&
          toggledOptions.length === 1
        ) {
          setToggledOptions([]);
        } else {
          setToggledOptions([componentProps.data.value]);
        }
      }
    }
    // When selecting keywords, always put focus back to Select field
    // itself, so that we can copy/paste etc
    props.focus(props.name);
    setLastClickTime(now);
    setLastClickedKeyword(clickedKeyword);
  };

  return (
    <CreatableSelect
      {...props}
      onBlur={() => setToggledOptions([])}
      className="creatable-select"
      classNamePrefix="react-select"
      components={{
        ...props.components,
        ValueContainer: (componentProps) => (
          <ValueContainer {...componentProps} error={!!props.error} />
        ),
        MultiValueContainer,
        MultiValue: (componentProps) => (
          <ClickableMultiValue
            {...componentProps}
            onClick={handleMultiValueClick}
            editingKeyword={editingKeyword}
          />
        ),
        Input: (componentProps) => (
          <Input
            {...componentProps}
            id={props.inputId}
            onPaste={props.onKeywordsPaste}
            onCreateOption={props.onCreateOption}
            canCreateOption={props.canCreateOption}
            onRemoveKeyword={props.onRemoveKeyword}
            isDisabled={props.isDisabled}
            keyword={editingKeyword}
            setEditingKeyword={setEditingKeyword}
          />
        ),
      }}
      onKeyDown={handleKeyDown}
      styles={{
        ...props.styles,
        multiValue: (base, state) => {
          const userMulti = props.styles?.multiValue
            ? props.styles.multiValue(base, state)
            : base;

          const isToggled = toggledOptions.includes(
            (state.data as Option).value
          );

          const backgroundColor = isToggled
            ? props.toggledBackgroundColor
            : (userMulti.backgroundColor ?? base.backgroundColor);

          const color = userMulti.color ?? base.color;
          return {
            ...userMulti,
            backgroundColor,
            color,
          };
        },
      }}
    />
  );
};

export default CreatableSelectWithCopyPaste;
